import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, Loader2, RefreshCw, Sparkles, Eye, X } from "lucide-react";
import { toast } from "sonner";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { BILLING_STATUS_LABELS, BILLING_STATUS_STYLES, DELIVERY_LABELS, MONTHS_ID, formatRupiah } from "@/lib/billingMeta";

const STATUS_FILTERS = [
  { v: "all", l: "Semua" },
  { v: "draft", l: "Draft" },
  { v: "published", l: "Terbit" },
  { v: "sent", l: "Terkirim" },
  { v: "paid", l: "Lunas" },
  { v: "cancelled", l: "Batal" },
];

function GenerateModal({ open, onClose, onDone, properties }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [propId, setPropId] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post("/billing/generate", {
        month: Number(month), year: Number(year), property_id: propId || null,
      });
      toast.success(data.message || "Billing digenerate.");
      onDone?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4" data-testid="billing-generate-modal" onClick={onClose}>
      <div className="bg-card rounded-2xl border border-border w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><Sparkles size={18} className="text-primary" /> Generate Billing</h3>
          <button onClick={onClose} data-testid="billing-generate-close" className="p-1.5 rounded-lg hover:bg-secondary/60"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-sm text-muted-foreground">Sistem membuat tagihan <span className="font-semibold text-foreground">draft</span> untuk penghuni yang aktif pada periode terpilih. Draft dapat diperiksa & diedit sebelum dipublish.</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Bulan</label>
              <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="billing-generate-month"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30">
                {MONTHS_ID.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Tahun</label>
              <input type="number" value={year} onChange={(e) => setYear(e.target.value)} data-testid="billing-generate-year"
                className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Properti (opsional)</label>
            <select value={propId} onChange={(e) => setPropId(e.target.value)} data-testid="billing-generate-property"
              className="w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30">
              <option value="">Semua Properti</option>
              {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <button onClick={run} disabled={busy} data-testid="billing-generate-run"
            className="w-full py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Generate Draft
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OwnerBillingPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [propId, setPropId] = useState("all");
  const [month, setMonth] = useState("all");
  const [q, setQ] = useState("");
  const [properties, setProperties] = useState([]);
  const [genOpen, setGenOpen] = useState(false);

  const load = () => {
    setLoading(true);
    apiClient.get("/billing").then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    apiClient.get("/properties").then((r) => setProperties(r.data || [])).catch(() => {});
  }, []);

  const propMap = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => { if (r.property_id) m.set(r.property_id, r.property_name || "Properti"); });
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (propId !== "all" && r.property_id !== propId) return false;
      if (month !== "all" && r.period_month !== Number(month)) return false;
      if (term && !(r.tenant_name || "").toLowerCase().includes(term) && !(r.invoice_no || "").toLowerCase().includes(term) && !(r.room_number || "").toLowerCase().includes(term)) return false;
      return true;
    });
  }, [rows, status, propId, month, q]);

  return (
    <OwnerLayout>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Receipt size={22} className="text-primary" /> Billing</h1>
          <p className="text-sm text-muted-foreground">Tagihan penghuni: sewa kamar prorata, listrik, dan biaya lain.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setGenOpen(true)} data-testid="billing-generate-open"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:scale-[1.03] active:scale-95 transition-transform">
            <Sparkles size={15} /> Generate Billing
          </button>
          <button onClick={load} data-testid="billing-refresh"
            className="grid place-items-center w-10 h-10 rounded-full border border-border hover:bg-secondary/60 transition-colors">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="billing-status-filters">
        {STATUS_FILTERS.map((f) => (
          <button key={f.v} onClick={() => setStatus(f.v)} data-testid={`billing-filter-${f.v}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition-colors ${status === f.v ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary/60"}`}>
            {f.l}
          </button>
        ))}
      </div>
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <select value={propId} onChange={(e) => setPropId(e.target.value)} data-testid="billing-property-filter"
          className="rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary">
          <option value="all">Semua Properti</option>
          {propMap.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={month} onChange={(e) => setMonth(e.target.value)} data-testid="billing-month-filter"
          className="rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary">
          <option value="all">Semua Bulan</option>
          {MONTHS_ID.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="billing-search" placeholder="Cari invoice / penghuni / kamar"
          className="flex-1 rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary" />
      </div>

      {loading ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-primary" size={26} /></div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground" data-testid="billing-empty">
          <Receipt size={30} className="mx-auto mb-2" /> Belum ada tagihan. Klik "Generate Billing" untuk membuat.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border" data-testid="billing-table">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="bg-secondary/50 text-left">
              <tr className="[&>th]:px-4 [&>th]:py-3 [&>th]:font-semibold [&>th]:text-muted-foreground">
                <th>Invoice</th><th>Penghuni</th><th>Kamar</th><th>Periode</th><th>Hari</th><th>Total</th><th>Status</th><th>Kirim</th><th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b, i) => (
                <tr key={b.id} data-testid={`billing-row-${i}`} className="border-t border-border [&>td]:px-4 [&>td]:py-3">
                  <td className="font-mono text-xs">{b.invoice_no}</td>
                  <td className="font-semibold">{b.tenant_name || "-"}</td>
                  <td>{b.room_number || "-"}</td>
                  <td>{b.period_label}</td>
                  <td>{b.billed_days}{b.is_prorated ? " (prorata)" : ""}</td>
                  <td className="font-semibold">{formatRupiah(b.total)}</td>
                  <td><span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${BILLING_STATUS_STYLES[b.status] || ""}`} data-testid={`billing-row-status-${i}`}>{BILLING_STATUS_LABELS[b.status] || b.status}</span></td>
                  <td className="text-xs text-muted-foreground">{DELIVERY_LABELS[b.delivery_status] || b.delivery_status}</td>
                  <td>
                    <button onClick={() => navigate(`/owner/billing/${b.id}`)} data-testid={`billing-row-view-${i}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border text-xs font-semibold hover:bg-secondary/60 transition-colors">
                      <Eye size={14} /> Detail
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <GenerateModal open={genOpen} onClose={() => setGenOpen(false)} onDone={load} properties={properties} />
    </OwnerLayout>
  );
}
